import {
  FrameStencil,
  HandoffAction,
  NonHandoffPossessionAction,
  PlayerAction,
  PossessionAction,
} from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestHandoff, findClosestPath } from "../find-closest";
import {
  getBallActions,
  getBallActionsNoNext,
  getBallMarkersNoMovement,
  getNonAssociatedBallAndPossessionActions,
  getNonAssociatedHandoffActions,
  getNonAssociatedPlayerAndPossessionActions,
  getPlayerActions,
  getPlayerActionsNoNext,
  getPlayerMarkers,
  getPlayerMarkersNoMovement,
  getPossessionActionsNoNextBall,
  getPossessionActionsNoNextPlayer,
  isReceivingHandoffLayer,
} from "../frame-stencil-filters";
import { refreshMovements } from "../refresh-movements";
import { checkActionEndPoint } from "./check-action-end-point";
import { checkPlayerPossessionActionStartPoint } from "./check-player-possession-action-start-point";
import { getHandoffStartTimes } from "./get-handoff-start-times";

type AddedPossessionAction = Omit<PossessionAction, "startTime" | "endTime">;

export interface AddPossessionActionProps {
  action: AddedPossessionAction;
  stencils: FrameStencil[];
}

export const addPossessionAction = ({ action, stencils }: AddPossessionActionProps): FrameStencil[] => {
  // get all players that don't have a movement and playerActions that don't have a nextPlayerAction
  const playersAndPlayerActionsNoNext = [
    ...getPlayerMarkersNoMovement(stencils),
    ...getPlayerActionsNoNext(stencils),
    ...getPossessionActionsNoNextPlayer(stencils),
  ];

  const { action: actionWithPlayerTimes, stencils: stencilsWithUpdatedPlayers } =
    checkPlayerPossessionActionStartPoint<PossessionAction>({
      stencils,
      stencilsToCheck: playersAndPlayerActionsNoNext,
      action: action,
    });

  const ballsAndBallActionsNoNext = [
    ...getBallMarkersNoMovement(stencilsWithUpdatedPlayers),
    ...getBallActionsNoNext(stencilsWithUpdatedPlayers),
    ...getPossessionActionsNoNextBall(stencilsWithUpdatedPlayers),
  ];

  const { action: actionWithBallTimes, stencils: stencilsWithUpdatedBalls } =
    checkPlayerPossessionActionStartPoint<PossessionAction>({
      action: actionWithPlayerTimes,
      stencilsToCheck: ballsAndBallActionsNoNext,
      stencils: stencilsWithUpdatedPlayers,
    });

  const { next: nextPlayerAction, newStencils: newPlayerStencils } = checkActionEndPoint({
    action: actionWithBallTimes,
    actionsToCheck: getNonAssociatedPlayerAndPossessionActions(stencilsWithUpdatedBalls),
    stencils: stencilsWithUpdatedBalls,
  });

  const actionWithNextPlayer: PossessionAction = {
    ...actionWithBallTimes,
    nextPlayerAction: nextPlayerAction?.id || null,
  };

  const { next: nextBallAction, newStencils: newBallStencils } = checkActionEndPoint({
    action: actionWithNextPlayer,
    actionsToCheck: getNonAssociatedBallAndPossessionActions(newPlayerStencils),
    stencils: newPlayerStencils,
  });

  const actionWithNextBall: PossessionAction = { ...actionWithNextPlayer, nextBallAction: nextBallAction?.id || null };

  const stencilsWithRefreshedMovements = refreshMovements([...newBallStencils, actionWithNextBall]);

  const nextBallActionWithPrevPlayer = (() => {
    const updatedNextBallAction = getBallActions(stencilsWithRefreshedMovements).find(
      (s) => s.id === nextBallAction?.id
    );
    if (!updatedNextBallAction) return null;

    const playerOnNewPossession = getPlayerMarkers(stencilsWithRefreshedMovements).find((p) =>
      p.movements.some((m) => m.id === actionWithNextBall.id)
    );

    if (!playerOnNewPossession) return updatedNextBallAction;

    return { ...updatedNextBallAction, prevPlayer: playerOnNewPossession.id };
  })();

  const stencilsWithNextBallAction = nextBallActionWithPrevPlayer
    ? sliceItemUpdate({ arr: stencilsWithRefreshedMovements, item: nextBallActionWithPrevPlayer })
    : stencilsWithRefreshedMovements;

  const stencilsWithHandoffUpdates = ((): FrameStencil[] => {
    if (isReceivingHandoffLayer(actionWithNextBall)) return stencilsWithNextBallAction;

    // closest handoff to possession
    const closestHandoff =
      actionWithNextBall.nextBallAction === null
        ? findClosestHandoff({
            stencils: getNonAssociatedHandoffActions(stencilsWithNextBallAction),
            target: actionWithNextBall,
          })
        : null;

    if (!closestHandoff) return stencilsWithNextBallAction;

    const playerIntersection = findClosestPath({
      stencils: getPlayerActions(stencilsWithNextBallAction).filter((s) => s.id === closestHandoff?.action?.nextAction),
      target: closestHandoff.action.pos,
    });

    const { possessionStartTime, playerStartTime, handoffStartTime } = getHandoffStartTimes({
      possessionIntersection: { ...closestHandoff, action: actionWithNextBall },
      playerIntersection,
      handoff: closestHandoff.action,
    });

    const updatedPossessionAction: NonHandoffPossessionAction = {
      ...actionWithNextBall,
      startTime: possessionStartTime,
      endTime: possessionStartTime + (actionWithNextBall.endTime - actionWithNextBall.startTime),
      nextBallAction: closestHandoff.action.id || null,
    };

    const updatedPlayerAction: PlayerAction | null = playerIntersection
      ? {
          ...playerIntersection.action,
          startTime: playerStartTime,
          endTime: playerStartTime + (playerIntersection.action.endTime - playerIntersection.action.startTime),
        }
      : null;

    const playerOnNewPossession = getPlayerMarkers(stencilsWithNextBallAction).find((p) =>
      p.movements.some((m) => m.id === actionWithNextBall.id)
    );
    const updatedHandoff: HandoffAction = {
      ...closestHandoff.action,
      prevAction: actionWithNextBall.id,
      prevPlayer: playerOnNewPossession?.id || null,
      startPos: closestHandoff.intersection,
      startTime: handoffStartTime,
      endTime: handoffStartTime + (closestHandoff.action.endTime - closestHandoff.action.startTime),
    };

    return [updatedPossessionAction, updatedPlayerAction, updatedHandoff]
      .filter((s): s is NonHandoffPossessionAction | PlayerAction | HandoffAction => s !== null)
      .reduce((acc, s) => sliceItemUpdate({ arr: acc, item: s }), stencilsWithNextBallAction);
  })();

  return refreshMovements(stencilsWithHandoffUpdates);
};
